import React, { useState } from "react";
import {
  CollapsableItemList,
  Container,
  Layout,
  MultiSearch,
  Padding,
  Sheet,
  Text,
  Modal,
  Input,
  Button,
} from "@kidslivesafe/sc-react";
import "./neighborhoodHome.css";
import { useMultiSearchProps } from "../../../hooks/multiSearchPropsHook";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  selectReportHistoryStatus,
  selectNeighborhoodReportHistory,
} from "../../../features/reports/reportsSlice";
import ActivityHistoryLineItem from "../../../components/UIComponents/ActivityHistory/ActivityHistoryLineItem";
import NeighborhoodHistoryMap from "./NeighborhoodHistoryMap";

import MonitoringContainer from "../../../components/UIComponents/MonitoringContainer/MontitoringContainer";

import {
  selectRenameReport,
  selectToggleMonitoringModal,
  setToggleMonitoringModal,
  updateNeighborhoodWatchMonitoringAreaAsync,
  updatePropertyMonitoringAsync,
} from "../../../features/monitoring/monitoringSlice";
import SpacerMultiSearch from "../../../components/UIComponents/SpacerMultiSearch/SpacerMultiSearch";

const NeighborhoodHome: React.FC = () => {
  const dispatch = useAppDispatch();

  const reportHistory = useAppSelector(selectNeighborhoodReportHistory);
  const reportHistoryStatus = useAppSelector(selectReportHistoryStatus);

  const loading = reportHistoryStatus === "loading";

  const monitoringModal = useAppSelector(selectToggleMonitoringModal);

  const [nickname, setNickname] = useState<string>("");
  const [nicknameError, setNickNameError] = useState<boolean>(false);
  const renameReport = useAppSelector(selectRenameReport);

  const multiSearchProps = useMultiSearchProps(
    {
      footer: true,
      showCancel: false,
      topPadding: "xs",
      resetInput: true,
      buttonText: "New Search",
      openTab: "neighborhood",
      tabs: ["neighborhood"],
    },
    "neighborhood-home",
  );

  const handleSubmitNickName = (nickname: string) => {
    if (nickname.length > 15) {
      setNickNameError(true);
    } else {
      setNickNameError(false);
    }
  };

  return (
    <>
      <Container>
        <Sheet>
          <Padding left="md" right="md" top="md">
            <Layout type="flex" flexProps={{ gap: "xs", direction: "column" }}>
              <Text tag="h3" textAlign="center">
                Neighborhood Search
              </Text>
              <Text size="sm" textAlign="center">
                Search for and monitor offenders in your area.{" "}
              </Text>
            </Layout>
          </Padding>
        </Sheet>
      </Container>
      <MultiSearch {...multiSearchProps} />
      <Sheet>
        <Padding space="md">
          <Layout type="flex" flexProps={{ gap: "xs", direction: "column" }}>
            <Container
              containerTitle="Neighborhood Report History"
              containerId="neighborhood-home__activity-history__container"
            >
              <NeighborhoodHistoryMap />
              <CollapsableItemList numberToShow={5}>
                {loading ? (
                  Array(5)
                    .fill(null)
                    .map((_, index) => (
                      <ActivityHistoryLineItem
                        report={undefined}
                        loading={loading}
                        loadingKey={index}
                        key={index}
                      />
                    ))
                ) : reportHistory && reportHistory.length > 0 ? (
                  reportHistory.map(report =>
                    report ? (
                      <ActivityHistoryLineItem
                        report={report}
                        loadingKey={report.score}
                        key={report.score}
                      />
                    ) : null,
                  )
                ) : (
                  <Padding space="md">
                    <Text size="xs">
                      Get started by searching an address above.
                    </Text>
                  </Padding>
                )}
              </CollapsableItemList>
            </Container>
            <MonitoringContainer
              monitoringType={"Neighborhood"}
              containerId={"neighborhood-home__report-monitoring__container"}
            />
          </Layout>
        </Padding>
        <SpacerMultiSearch options={["people", "property", "phone"]} />
      </Sheet>
      <Modal
        open={monitoringModal}
        setOpen={() => {
          dispatch(setToggleMonitoringModal(!monitoringModal));
          setNickname("");
        }}
        size="fit-content"
        xClose
        outSideClose
      >
        <Padding space="md">
          <Layout
            type="flex"
            flexProps={{
              direction: "column",
              gap: "lg",
              alignItems: "center",
            }}
          >
            <Text tag="h2">Set New Name</Text>
            <Text size="xs" tag="i">
              Please use 15 characters maximum
            </Text>
            <Layout
              type="flex"
              flexProps={{
                gap: "sm",
                direction: "column",
                alignItems: "center",
              }}
            >
              <Input
                id="neighborhood-report__monitoring-modal"
                value={nickname}
                handleChange={input => {
                  setNickname(input);
                  handleSubmitNickName(input);
                }}
                type="text"
                placeholder={
                  renameReport && renameReport.type === "property"
                    ? "Property"
                    : "Neighborhood"
                }
                fill="fill"
                error={nicknameError}
              />
              <Button
                buttonText="Set Name"
                fill="fill"
                disabled={nicknameError}
                handleClick={() => {
                  if (renameReport) {
                    if (renameReport.type === "neighborhood") {
                      dispatch(
                        updateNeighborhoodWatchMonitoringAreaAsync({
                          address: renameReport.address,
                          addressLine2: renameReport.addressLine2,
                          city: renameReport.city,
                          state: renameReport.state,
                          zipCode: renameReport.zipCode,
                          nickname: nickname,
                          radius: 5,
                          id: renameReport.id,
                          latestOffendersCount:
                            renameReport?.latestOffendersCount,
                        }),
                      );
                    } else if (renameReport.type === "property") {
                      dispatch(
                        updatePropertyMonitoringAsync({
                          addressLine1: renameReport.addressLine1,
                          addressLine2: renameReport.addressLine2,
                          city: renameReport.city,
                          state: renameReport.state,
                          zip: renameReport.zip,
                          nickname: nickname,
                        }),
                      );
                    }
                  }
                  dispatch(setToggleMonitoringModal(!monitoringModal));
                  setNickname("");
                }}
              />
            </Layout>
          </Layout>
        </Padding>
      </Modal>
    </>
  );
};

export default NeighborhoodHome;
